import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  FormControl, InputLabel, Select, MenuItem, Grid, Alert,
  Switch, FormControlLabel, Divider
} from '@mui/material';
import { Save, ArrowBack, Publish } from '@mui/icons-material';
import { getPage, createPage, updatePage, getCategories, getPages } from '../services/api';

function PageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [page, setPage] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    template: 'default',
    category: '',
    parent: '',
    status: 'draft',
    showInNav: false,
    navLabel: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: []
  });
  const [categories, setCategories] = useState([]);
  const [parentPages, setParentPages] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [catRes, pagesRes] = await Promise.all([
        getCategories(),
        getPages({ parent: 'null' })
      ]);
      setCategories(catRes.data);
      setParentPages(pagesRes.data.filter(p => p._id !== id));

      if (!isNew) {
        const { data } = await getPage(id);
        setPage({
          ...data,
          category: data.category?._id || '',
          parent: data.parent?._id || '',
          metaKeywords: data.metaKeywords || []
        });
      }
    } catch (error) {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setPage({ ...page, [field]: value });

    // Auto-generate slug from title
    if (field === 'title' && isNew) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setPage(prev => ({ ...prev, slug }));
    }
  };

  const handleSave = async (publish = false) => {
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const data = { ...page };
      if (publish) data.status = 'published';
      if (!data.category) delete data.category;
      if (!data.parent) delete data.parent;

      if (isNew) {
        const { data: newPage } = await createPage(data);
        setSuccess('Page creee avec succes');
        navigate(`/pages/${newPage._id}`);
      } else {
        await updatePage(id, data);
        setSuccess('Page mise a jour');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/pages')}>
            Retour
          </Button>
          <Typography variant="h4" fontWeight="bold">
            {isNew ? 'Nouvelle page' : 'Modifier la page'}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Save />}
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            Enregistrer
          </Button>
          <Button
            variant="contained"
            startIcon={<Publish />}
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            Publier
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <TextField
                fullWidth
                label="Titre"
                value={page.title}
                onChange={(e) => handleChange('title', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Slug (URL)"
                value={page.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                margin="normal"
                helperText={`URL: /${page.slug}`}
              />
              <TextField
                fullWidth
                label="Extrait"
                value={page.excerpt}
                onChange={(e) => handleChange('excerpt', e.target.value)}
                margin="normal"
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Contenu"
                value={page.content}
                onChange={(e) => handleChange('content', e.target.value)}
                margin="normal"
                multiline
                rows={20}
                placeholder="Contenu de la page (HTML ou Markdown)"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Publication</Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={page.status}
                  label="Statut"
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <MenuItem value="draft">Brouillon</MenuItem>
                  <MenuItem value="review">En revue</MenuItem>
                  <MenuItem value="published">Publie</MenuItem>
                  <MenuItem value="archived">Archive</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Template</InputLabel>
                <Select
                  value={page.template}
                  label="Template"
                  onChange={(e) => handleChange('template', e.target.value)}
                >
                  <MenuItem value="default">Par defaut</MenuItem>
                  <MenuItem value="maladie">Fiche maladie</MenuItem>
                  <MenuItem value="centre">Centre de reference</MenuItem>
                  <MenuItem value="ressource">Ressource</MenuItem>
                  <MenuItem value="contact">Contact</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Organisation</Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>Categorie</InputLabel>
                <Select
                  value={page.category}
                  label="Categorie"
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  <MenuItem value="">Aucune</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Page parente</InputLabel>
                <Select
                  value={page.parent}
                  label="Page parente"
                  onChange={(e) => handleChange('parent', e.target.value)}
                >
                  <MenuItem value="">Aucune</MenuItem>
                  {parentPages.map((p) => (
                    <MenuItem key={p._id} value={p._id}>{p.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Navigation</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={page.showInNav}
                    onChange={(e) => handleChange('showInNav', e.target.checked)}
                  />
                }
                label="Afficher dans la navigation"
              />
              {page.showInNav && (
                <TextField
                  fullWidth
                  label="Label navigation"
                  value={page.navLabel}
                  onChange={(e) => handleChange('navLabel', e.target.value)}
                  margin="normal"
                  placeholder={page.title}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>SEO</Typography>
              <TextField
                fullWidth
                label="Meta titre"
                value={page.metaTitle}
                onChange={(e) => handleChange('metaTitle', e.target.value)}
                margin="normal"
                placeholder={page.title}
              />
              <TextField
                fullWidth
                label="Meta description"
                value={page.metaDescription}
                onChange={(e) => handleChange('metaDescription', e.target.value)}
                margin="normal"
                multiline
                rows={2}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default PageEditor;
